'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams, useRouter } from 'next/navigation';
import React from 'react';

import { ConfirmDialog } from '@ui/components/common/dialog/confirmDialog';
import { useToast } from '@ui/components/common/toast';
import { BrowserApiClient } from '@website/lib/api/browser';

interface DeleteActivityDialogProps {
  activityId: string;
  activityTitle: string;
  onSuccess?: () => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteActivityDialog({
  activityId,
  activityTitle,
  onSuccess,
  isOpen,
  onOpenChange
}: DeleteActivityDialogProps) {
  const t = useTranslations('ActivityDetail');
  const router = useRouter();
  const queryClient = useQueryClient();
  const { success: showSuccessToast, error: showErrorToast } = useToast();
  const params = useParams();
  const locale = params.locale as string;
  const deleteActivityMutation = useMutation({
    mutationFn: async () => {
      return await BrowserApiClient.removeActivity(activityId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['activity', activityId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });

      showSuccessToast({
        title: t('deleteSuccess'),
        description: t('deleteSuccessDescription')
      });

      onOpenChange(false);

      // Call custom success handler or redirect
      if (onSuccess) {
        onSuccess();
      }
      router.replace(`/${locale}/activity`);
    },
    onError: (error: Error) => {
      showErrorToast({
        title: t('deleteError'),
        description: error.message || t('deleteErrorDescription')
      });
    }
  });

  return (
    <>
      <ConfirmDialog
        isOpen={isOpen}
        onClose={() => onOpenChange(false)}
        onConfirm={async () => {
          await deleteActivityMutation.mutateAsync();
        }}
        title={t('confirmDelete')}
        description={`${t('confirmDeleteDescription', { title: activityTitle })}\n\n${t('deleteWarning')}`}
        rejectButtonText={t('cancel')}
        confirmButtonText={deleteActivityMutation.isPending ? t('deleting') : t('delete')}
        icon={
          <div className="w-20 h-20 rounded-full bg-error-100 flex items-center justify-center">
            <Trash2 className="w-10 h-10 text-error-600" />
          </div>
        }
      />
    </>
  );
}
